import React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTime, Duration } from 'luxon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function LabDetails(props) {
    const zeroTime = DateTime.fromISO("0001-01-01T00:00:00Z");
    const zeroTimeRemaining = Duration.fromObject({ seconds: 0 });
    const [timeRemaining, setTimeRemaining] = React.useState(zeroTimeRemaining);
    const [launchingLab, setLaunchingLab] = React.useState(false);

    const getLaunchState = lab => lab == null ? true : !DateTime.fromISO(lab.launchTime).equals(zeroTime);
    const getConsoleState = lab => lab == null ? true : DateTime.now() > DateTime.fromISO(lab.expiryTime);
    const getConsoleLink = lab => {
        let consoleLink = "https://";

        if (lab != null) {
            if (lab.environment == "google") {
                consoleLink = "https://console.cloud.google.com/home/dashboard?project=" + lab.instanceName + "&authuser=" + props.user.email;
            }
        }

        return consoleLink;
    };

    const launchLab = lab => () => {
        setLaunchingLab(true);

        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/my/assignedLabs/" + lab.id, {
            headers: {
                Authorization: "Bearer " + props.token
            },
            method: "POST"
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(result => {
            props.setLab(result);
            props.getLabs();
            props.notify(null, "success", "Launched lab " + lab.displayName);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
        }).finally(() => {
            setLaunchingLab(false);
        });
    }

    const closeLabDialog = () => {
        props.setLab(null);
    };

    React.useEffect(() => {
        const interval = setInterval(() => {
            const lab = props.lab;

            if (lab == null) {
                clearInterval(interval);
                setTimeRemaining(zeroTimeRemaining);
                return;
            }

            const launchTime = DateTime.fromISO(lab.launchTime);

            if (launchTime.equals(zeroTime)) {
                clearInterval(interval);
                setTimeRemaining(Duration.fromObject({ minutes: lab.minutes }));
                return;
            }

            const expiryTime = DateTime.fromISO(lab.expiryTime);
            const remainingTime = expiryTime.diffNow(["seconds"]);

            if (remainingTime.toObject().seconds < 0) {
                clearInterval(interval);
                setTimeRemaining(zeroTimeRemaining);
            } else {
                setTimeRemaining(remainingTime);
            }
        });

        return () => clearInterval(interval);
    }, [props.lab]);

    return (
        <Dialog open={props.lab != null} maxWidth="md" fullWidth>
            <DialogTitle>Lab details</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    <Typography variant="button">
                        ID
                    </Typography>
                </DialogContentText>
                <DialogContentText>
                    {props.lab == null ? "" : props.lab.id}
                </DialogContentText>
                <br />
                <DialogContentText>
                    <Typography variant="button">
                        Length
                    </Typography>
                </DialogContentText>
                <DialogContentText>
                    {props.lab == null ? "" : props.lab.minutes} minutes
                </DialogContentText>
                <br />
                <DialogContentText>
                    <Typography variant="button">
                        Instructions
                    </Typography>
                </DialogContentText>
                <ReactMarkdown components={{p: DialogContentText}} remarkPlugins={[remarkGfm]}>
                    {props.lab == null ? "" : props.lab.labInstructions}
                </ReactMarkdown>
                <br />
                <DialogContentText>
                    <Typography variant="button">
                        Time remaining
                    </Typography>
                </DialogContentText>
                <DialogContentText>
                    {timeRemaining.toFormat("hh:mm:ss")}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" disabled={getLaunchState(props.lab) || launchingLab} onClick={launchLab(props.lab)}>
                        Launch lab
                        <Box sx={{ display: launchingLab ? "contents" : "none" }}>
                            &nbsp;<CircularProgress size="1rem" color="inherit" />
                        </Box>
                    </Button>
                    <Button variant="contained" disabled={getConsoleState(props.lab)} href={getConsoleLink(props.lab)} target="gcp">Open console</Button>
                    <Button variant="contained" color="error" onClick={closeLabDialog}>Close</Button>
                </Stack>
            </DialogActions>
        </Dialog>
    )
}

function LabList(props) {
    const zeroTime = DateTime.fromISO("0001-01-01T00:00:00Z");
    const [assignedLabs, setAssignedLabs] = React.useState([]);
    const [lab, setLab] = React.useState(null);

    const columns = [
        { field: "displayName", headerName: "Lab name", flex: 1 },
        { field: "launchTime", headerName: "Launched", flex: 1, renderCell: params => {
            let launchTime = DateTime.fromISO(params.value);

            if (launchTime.equals(zeroTime)) {
                return "No";
            } else {
                return "Yes";
            }
        }},
        { field: "expiryTime", headerName: "Active", flex: 1, renderCell: params => {
            let expiryTime = DateTime.fromISO(params.value);
            let now = DateTime.now();

            if (now <= expiryTime) {
                return "Yes";
            } else {
                return "No";
            }
        }},
        { field: "actions", type: "actions", getActions: params => [
            <GridActionsCellItem icon={<AssignmentIcon />} label="Lab details" onClick={showLabDetails(params.row)} />
        ]}
    ];

    const getLabs = () => {
        fetch(window.__RUNTIME_CONFIG__.API_URL + "/v1/my/assignedLabs", {
            headers: {
                Authorization: "Bearer " + props.token
            }
        }).then(response => response.ok ? response.json() : response.json().then(response => Promise.reject(response))).then(result => {
            setAssignedLabs(result);
        }).catch(error => {
            let message = error.error == null ? error.message : error.error;

            props.notify(null, "error", message);
            setAssignedLabs([]);
        });
    };

    React.useEffect(getLabs, [props.token]);

    const showLabDetails = lab => () => {
        setLab(lab);
    };

    return (
        <Box>
            <DataGrid autoHeight columns={columns} rows={assignedLabs} getRowHeight={() => "auto" } />
            <LabDetails lab={lab} token={props.token} setLab={setLab} getLabs={getLabs} user={props.user} notify={props.notify} />
        </Box>
    )
}

function Home(props) {
    if (props.user == null) {
        return (
            <Container>
                Please login
            </Container>
        )
    } else {
        return (
            <Container>
                <LabList user={props.user} token={props.token} notify={props.notify} />
            </Container>
        )
    }
}

export default Home;